/**
 * Vista Theme - Main JavaScript File
 * Shopify 2.0 Theme
 * Version: 1.0.0
 */

/**
 * ============================================================================
 * INTERSECTION OBSERVER SCROLL ANIMATIONS
 * ============================================================================
 */
function initScrollAnimations() {
  const observerOptions = {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');

        // Handle stagger-children animations
        if (entry.target.classList.contains('stagger-children')) {
          const children = entry.target.querySelectorAll('[class*="stagger-item"]');
          children.forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('is-visible');
            }, index * 100);
          });
        }
      }
    });
  }, observerOptions);

  // Observe all animation elements
  document.querySelectorAll('.fade-in-up, .fade-in, .stagger-children').forEach(el => {
    observer.observe(el);
  });
}

/**
 * ============================================================================
 * AJAX CART FUNCTIONALITY
 * ============================================================================
 */
const CartManager = (() => {
  const cartDrawerId = 'cart-drawer';
  const cartBadgeSelector = '[data-cart-count]';
  const cartDrawerContentSelector = '[data-cart-content]';

  /**
   * Add item to cart
   */
  async function addToCart(variantId, quantity = 1, sellingPlanId = null) {
    try {
      const body = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };

      if (sellingPlanId) {
        body.items[0].selling_plan = sellingPlanId;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await updateCartUI();
        openCartDrawer();
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  /**
   * Update cart item quantity
   */
  async function updateCart(key, quantity) {
    try {
      const body = {
        updates: {
          [key]: quantity
        }
      };

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await updateCartUI();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  /**
   * Remove item from cart
   */
  async function removeFromCart(key) {
    try {
      const body = {
        updates: {
          [key]: 0
        }
      };

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await updateCartUI();
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }

  /**
   * Get current cart data
   */
  async function getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error getting cart:', error);
      return null;
    }
  }

  /**
   * Update cart UI (drawer content, badge count, shipping bar)
   */
  async function updateCartUI() {
    const cart = await getCart();
    if (!cart) return;

    updateCartBadge(cart.item_count);
    updateCartDrawerContent(cart);
    updateShippingBar(cart.total_price);
  }

  /**
   * Update cart count badge
   */
  function updateCartBadge(count) {
    const badges = document.querySelectorAll(cartBadgeSelector);
    badges.forEach(badge => {
      badge.textContent = count;
      if (count > 0) {
        badge.classList.add('has-items');
      } else {
        badge.classList.remove('has-items');
      }
    });
  }

  /**
   * Update cart drawer content
   */
  function updateCartDrawerContent(cart) {
    const contentContainer = document.querySelector(cartDrawerContentSelector);
    if (!contentContainer) return;

    if (cart.item_count === 0) {
      contentContainer.innerHTML = `
        <div class="cart-empty">
          <p>${window.theme.locale.cart.empty}</p>
        </div>
      `;
      return;
    }

    let itemsHTML = cart.items.map(item => `
      <div class="cart-item" data-cart-key="${item.key}">
        <div class="cart-item__image">
          <img src="${item.featured_image.url}" alt="${item.title}" loading="lazy">
        </div>
        <div class="cart-item__details">
          <h3 class="cart-item__title">${item.title}</h3>
          ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
        </div>
        <div class="cart-item__controls">
          <button class="quantity-button quantity-minus" data-action="decrease">−</button>
          <input type="number" class="quantity-input" value="${item.quantity}" min="1" data-action="update">
          <button class="quantity-button quantity-plus" data-action="increase">+</button>
        </div>
        <button class="cart-item__remove" data-action="remove" aria-label="Remove">✕</button>
      </div>
    `).join('');

    const subtotal = formatMoney(cart.total_price);

    contentContainer.innerHTML = `
      <div class="cart-items">
        ${itemsHTML}
      </div>
      <div class="cart-summary">
        <div class="cart-summary__row">
          <span>${window.theme.locale.cart.subtotal}</span>
          <span>${subtotal}</span>
        </div>
        <p class="cart-summary__note">${window.theme.locale.cart.shipping_note}</p>
        <a href="/cart" class="button button-primary">${window.theme.locale.cart.checkout}</a>
      </div>
    `;

    attachCartItemListeners();
  }

  /**
   * Attach event listeners to cart item controls
   */
  function attachCartItemListeners() {
    document.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.closest('.cart-item').dataset.cartKey;
        removeFromCart(key);
      });
    });

    document.querySelectorAll('.quantity-minus').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.nextElementSibling;
        const quantity = Math.max(1, parseInt(input.value) - 1);
        input.value = quantity;
        const key = btn.closest('.cart-item').dataset.cartKey;
        updateCart(key, quantity);
      });
    });

    document.querySelectorAll('.quantity-plus').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.previousElementSibling;
        const quantity = parseInt(input.value) + 1;
        input.value = quantity;
        const key = btn.closest('.cart-item').dataset.cartKey;
        updateCart(key, quantity);
      });
    });

    document.querySelectorAll('.quantity-input').forEach(input => {
      input.addEventListener('change', () => {
        const quantity = Math.max(1, parseInt(input.value));
        input.value = quantity;
        const key = input.closest('.cart-item').dataset.cartKey;
        updateCart(key, quantity);
      });
    });
  }

  /**
   * Open cart drawer with scroll lock
   */
  function openCartDrawer() {
    const drawer = document.getElementById(cartDrawerId);
    if (drawer) {
      drawer.classList.add('is-open');
      document.body.classList.add('scroll-lock');
    }
  }

  /**
   * Close cart drawer and remove scroll lock
   */
  function closeCartDrawer() {
    const drawer = document.getElementById(cartDrawerId);
    if (drawer) {
      drawer.classList.remove('is-open');
      document.body.classList.remove('scroll-lock');
    }
  }

  /**
   * Toggle cart drawer
   */
  function toggleCartDrawer() {
    const drawer = document.getElementById(cartDrawerId);
    if (drawer && drawer.classList.contains('is-open')) {
      closeCartDrawer();
    } else {
      openCartDrawer();
    }
  }

  /**
   * Update shipping progress bar
   */
  function updateShippingBar(totalPrice) {
    const threshold = window.theme.settings.free_shipping_threshold || 7500;
    const progress = Math.min((totalPrice / threshold) * 100, 100);

    const bar = document.querySelector('[data-shipping-bar]');
    const message = document.querySelector('[data-shipping-message]');

    if (bar) {
      bar.style.width = progress + '%';
    }

    if (message) {
      if (totalPrice >= threshold) {
        message.textContent = window.theme.locale.cart.free_shipping;
      } else {
        const remaining = threshold - totalPrice;
        message.textContent = `${window.theme.locale.cart.shipping_note} ${formatMoney(remaining)} more`;
      }
    }
  }

  /**
   * Format money value
   */
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  }

  return {
    addToCart,
    updateCart,
    removeFromCart,
    getCart,
    openCartDrawer,
    closeCartDrawer,
    toggleCartDrawer
  };
})();

/**
 * ============================================================================
 * PRODUCT GALLERY
 * ============================================================================
 */
function initProductGallery() {
  const mainImage = document.querySelector('[data-gallery-main]');
  const thumbnails = document.querySelectorAll('[data-gallery-thumb]');

  thumbnails.forEach(thumbnail => {
    thumbnail.addEventListener('click', (e) => {
      e.preventDefault();

      thumbnails.forEach(t => t.classList.remove('is-active'));
      thumbnail.classList.add('is-active');

      if (mainImage) {
        mainImage.style.opacity = '0.5';

        const newSrc = thumbnail.dataset.galleryThumb;
        const newAlt = thumbnail.dataset.alt || '';

        setTimeout(() => {
          mainImage.src = newSrc;
          mainImage.alt = newAlt;
          mainImage.style.opacity = '1';
        }, 150);
      }
    });
  });
}

/**
 * ============================================================================
 * VARIANT SELECTOR
 * ============================================================================
 */
function initVariantSelector() {
  const variantButtons = document.querySelectorAll('[data-variant-select]');
  const variantInput = document.querySelector('input[name="id"]');
  const priceDisplay = document.querySelector('[data-price-display]');
  const comparePriceDisplay = document.querySelector('[data-compare-price-display]');
  const availabilityDisplay = document.querySelector('[data-availability-display]');
  const addToCartBtn = document.querySelector('[data-add-to-cart]');

  variantButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      e.preventDefault();

      const variantId = button.dataset.variantId;
      const variantData = button.dataset.variantData ? JSON.parse(button.dataset.variantData) : null;

      button.parentElement.querySelectorAll('[data-variant-select]').forEach(btn => {
        btn.classList.remove('is-selected');
      });
      button.classList.add('is-selected');

      if (variantInput) {
        variantInput.value = variantId;
      }

      if (variantData && priceDisplay) {
        priceDisplay.textContent = formatMoney(variantData.price);
      }

      if (variantData && comparePriceDisplay) {
        if (variantData.compare_at_price > variantData.price) {
          comparePriceDisplay.textContent = formatMoney(variantData.compare_at_price);
          comparePriceDisplay.parentElement.classList.remove('hidden');
        } else {
          comparePriceDisplay.parentElement.classList.add('hidden');
        }
      }

      if (variantData && availabilityDisplay) {
        const available = variantData.available;
        availabilityDisplay.textContent = available ? 'In Stock' : 'Sold Out';
        if (addToCartBtn) {
          addToCartBtn.disabled = !available;
        }
      }

      if (history.replaceState) {
        const newUrl = `${window.location.pathname}?variant=${variantId}`;
        history.replaceState({}, '', newUrl);
      }
    });
  });
}

/**
 * ============================================================================
 * PURCHASE TYPE TOGGLE
 * ============================================================================
 */
function initPurchaseTypeToggle() {
  const oneTimeBtn = document.querySelector('[data-purchase-one-time]');
  const subscribeBtn = document.querySelector('[data-purchase-subscribe]');
  const sellingPlanContainer = document.querySelector('[data-selling-plan-options]');
  const sellingPlanInput = document.querySelector('input[name="selling_plan"]');
  const priceDisplay = document.querySelector('[data-price-display]');

  if (!oneTimeBtn || !subscribeBtn) return;

  oneTimeBtn.addEventListener('click', () => {
    oneTimeBtn.classList.add('is-active');
    subscribeBtn.classList.remove('is-active');

    if (sellingPlanContainer) {
      sellingPlanContainer.classList.add('hidden');
    }
    if (sellingPlanInput) {
      sellingPlanInput.value = '';
    }
  });

  subscribeBtn.addEventListener('click', () => {
    subscribeBtn.classList.add('is-active');
    oneTimeBtn.classList.remove('is-active');

    if (sellingPlanContainer) {
      sellingPlanContainer.classList.remove('hidden');
    }
  });

  const sellingPlanButtons = document.querySelectorAll('[data-selling-plan-select]');
  sellingPlanButtons.forEach(button => {
    button.addEventListener('click', () => {
      const planId = button.dataset.sellingPlanId;
      const discount = button.dataset.discount;

      sellingPlanButtons.forEach(btn => btn.classList.remove('is-selected'));
      button.classList.add('is-selected');

      if (sellingPlanInput) {
        sellingPlanInput.value = planId;
      }

      if (discount && priceDisplay) {
        const originalPrice = parseFloat(priceDisplay.dataset.originalPrice || 0);
        const discountedPrice = originalPrice * (1 - discount / 100);
        priceDisplay.textContent = formatMoney(discountedPrice * 100);
      }
    });
  });
}

/**
 * ============================================================================
 * PRODUCT TABS
 * ============================================================================
 */
function initProductTabs() {
  const tabs = document.querySelectorAll('[data-tab-button]');

  tabs.forEach(tab => {
    tab.addEventListener('click', (e) => {
      e.preventDefault();

      const tabName = tab.dataset.tabButton;
      const tabContent = document.querySelector(`[data-tab-content="${tabName}"]`);

      if (!tabContent) return;

      tabs.forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');

      document.querySelectorAll('[data-tab-content]').forEach(content => {
        content.classList.remove('is-active');
        content.style.height = '0';
      });

      tabContent.classList.add('is-active');
      tabContent.style.height = tabContent.scrollHeight + 'px';

      setTimeout(() => {
        tabContent.style.height = 'auto';
      }, 300);
    });
  });
}

/**
 * ============================================================================
 * MOBILE NAVIGATION
 * ============================================================================
 */
function initMobileNavigation() {
  const hamburgerBtn = document.querySelector('[data-hamburger-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const closeBtn = document.querySelector('[data-mobile-menu-close]');

  if (!hamburgerBtn || !mobileMenu) return;

  function toggleMobileMenu() {
    hamburgerBtn.classList.toggle('is-active');
    mobileMenu.classList.toggle('is-open');
    document.body.classList.toggle('scroll-lock');
  }

  hamburgerBtn.addEventListener('click', toggleMobileMenu);

  if (closeBtn) {
    closeBtn.addEventListener('click', toggleMobileMenu);
  }

  document.addEventListener('click', (e) => {
    if (!mobileMenu.contains(e.target) && !hamburgerBtn.contains(e.target)) {
      if (mobileMenu.classList.contains('is-open')) {
        toggleMobileMenu();
      }
    }
  });
}

/**
 * ============================================================================
 * STICKY ADD TO CART BAR
 * ============================================================================
 */
function initStickyAddToCartBar() {
  const purchaseModule = document.querySelector('[data-purchase-module]');
  const stickyBar = document.querySelector('[data-sticky-bar]');

  if (!purchaseModule || !stickyBar) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        stickyBar.classList.add('is-hidden');
      } else {
        stickyBar.classList.remove('is-hidden');
      }
    });
  }, {
    threshold: 0.5
  });

  observer.observe(purchaseModule);

  const stickyAddBtn = stickyBar.querySelector('[data-add-to-cart]');
  if (stickyAddBtn) {
    stickyAddBtn.addEventListener('click', () => {
      const variantInput = document.querySelector('input[name="id"]');
      const quantityInput = document.querySelector('input[name="quantity"]');

      if (variantInput) {
        const variantId = variantInput.value;
        const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
        CartManager.addToCart(variantId, quantity);
      }
    });
  }
}

/**
 * ============================================================================
 * QUANTITY SELECTORS
 * ============================================================================
 */
function initQuantitySelectors() {
  const decreaseButtons = document.querySelectorAll('[data-quantity-decrease]');
  const increaseButtons = document.querySelectorAll('[data-quantity-increase]');

  decreaseButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.nextElementSibling;
      const currentValue = parseInt(input.value) || 1;
      input.value = Math.max(1, currentValue - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  increaseButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.previousElementSibling;
      const currentValue = parseInt(input.value) || 1;
      input.value = currentValue + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  document.querySelectorAll('input[type="number"][name="quantity"]').forEach(input => {
    input.addEventListener('change', () => {
      if (parseInt(input.value) < 1) {
        input.value = 1;
      }
    });
  });
}

/**
 * ============================================================================
 * CART DRAWER INITIALIZATION
 * ============================================================================
 */
function initCartDrawer() {
  const cartToggleButtons = document.querySelectorAll('[data-cart-toggle]');
  const cartDrawer = document.getElementById('cart-drawer');
  const cartDrawerClose = document.querySelector('[data-cart-drawer-close]');

  cartToggleButtons.forEach(btn => {
    btn.addEventListener('click', CartManager.toggleCartDrawer);
  });

  if (cartDrawerClose) {
    cartDrawerClose.addEventListener('click', CartManager.closeCartDrawer);
  }

  if (cartDrawer) {
    cartDrawer.addEventListener('click', (e) => {
      if (e.target === cartDrawer) {
        CartManager.closeCartDrawer();
      }
    });
  }
}

/**
 * ============================================================================
 * UTILITY: FORMAT MONEY
 * ============================================================================
 */
function formatMoney(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(cents / 100);
}

/**
 * ============================================================================
 * INITIALIZATION
 * ============================================================================
 */
document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
  initProductGallery();
  initVariantSelector();
  initPurchaseTypeToggle();
  initProductTabs();
  initMobileNavigation();
  initStickyAddToCartBar();
  initQuantitySelectors();
  initCartDrawer();

  window.CartManager = CartManager;

  console.log('Vista Theme initialized');
});
